import { useCallback } from 'react';
import { AvatarTagsQuery } from '@avatar/graphql/filterAvatars/operation.generated';
import useAvatarClickHandler from '@avatar/hooks/useAvatarClickHandler';
import { handleAvatarSwap } from '@avatar/utils/studio/handleAvatarSwap';
import type { Actor } from '@avatar/types';
import { usePromptsContext } from 'context/prompts/PromptsContext';
import { useEventsContext } from 'context/events/EventsContext';
import { EventName } from 'services/eventTrackingServiceTypes';
import { useUserContextQuery } from 'context/user/operation.generated';
import { useCanReadTeamVoices } from 'abilities/voices';
import {
  ActorVariant,
  StudioProjectVersion,
} from '../../../../server/graphql/__generated.types';

// Helper function to create a stable swap handler that can be reused without recursion
const createAvatarSwapHandler = ({
  currentFromAvatar,
  editor,
  promptsDispatch,
  studioVersion,
  onSelect,
  tags,
}: {
  currentFromAvatar: { variant?: ActorVariant; variantVersion?: string };
  editor?: {
    hasCues?: boolean;
    hasReplacements?: boolean;
  };
  promptsDispatch: ReturnType<typeof usePromptsContext>[1];
  studioVersion: StudioProjectVersion;
  onSelect: (props: {
    actor: Actor;
    variant: ActorVariant | undefined;
    variantVersion: string | undefined;
    onClose?: () => void;
    swapContext?: {
      lostFeatures?: {
        cues?: boolean;
        replacements?: boolean;
      };
    };
  }) => void;
  tags: AvatarTagsQuery;
}) => {
  // Create a stable handler function that can be reused
  const handleAvatarSelection = ({
    actor: selectedActor,
    variant,
    variantVersion,
    onClose,
  }: {
    actor: Actor;
    variant?: ActorVariant | undefined;
    variantVersion?: string | undefined;
    onClose?: (() => void) | undefined;
  }) => {
    if (!onSelect || !currentFromAvatar || !variant || !variantVersion) {
      return;
    }

    handleAvatarSwap({
      fromAvatar: currentFromAvatar,
      toAvatar: {
        variant,
        variantVersion,
      },
      editor,
      selectedActor,
      variant,
      onClose,
      studioVersion,
      handleChangeAvatar: (
        newAvatar,
        location,
        newAvatarVariant,
        newAvatarVariantVersion,
        swapContext
      ) => {
        onSelect({
          actor: newAvatar,
          variant: newAvatarVariant,
          variantVersion: newAvatarVariantVersion,
          onClose,
          swapContext,
        });
      },
      onAvatarWarning: info => {
        promptsDispatch({
          type: 'OpenSwitchAvatarVersionPrompt',
          payload: {
            lostFeatures: info.lostFeatures,
            onSwitch: () => {
              onSelect({
                actor: info.selectedActor,
                variant: info.variant,
                variantVersion: info.variantVersion,
                onClose,
                swapContext: {
                  lostFeatures: info.lostFeatures,
                },
              });
            },
            onCancel: () => {
              onClose?.();
              // Reuse the same handler function instead of recursive call
              promptsDispatch({
                type: 'OpenAvatarModalPrompt',
                payload: {
                  activeActorId: info.selectedActor.id,
                  activeVariantId: info.variant?.id,
                  activeVariantVersion: info.variantVersion,
                  projectVersion: studioVersion,
                  onSelect: handleAvatarSelection, // Reuse the same handler
                  tags: {
                    avatarTags: tags?.avatarTags ?? [],
                  },
                },
              });
            },
          },
        });
      },
    });
  };

  return handleAvatarSelection;
};

type OnSelectProps = (props: {
  actor: Actor;
  variant: ActorVariant | undefined;
  variantVersion: string | undefined;
  onClose: () => void;
  // WIP
  swapContext?: {
    lostFeatures?: {
      cues?: boolean;
      replacements?: boolean;
    };
  };
}) => void;

type ModalPayload = {
  activeAvatarId?: number;
  activeVariantId?: number;
  /** Model name (Universal) or version string (V2) for dropdown pre-selection */
  activeVariantVersion?: string;
  studioVersion: StudioProjectVersion;
  tags?: AvatarTagsQuery;
  fromAvatar?: {
    variant?: ActorVariant;
    variantVersion?: string;
  };
  editor?: {
    hasCues?: boolean;
    hasReplacements?: boolean;
  };
  onSelect?: OnSelectProps;
};

const useStudioAvatarModal = ({
  setActiveAvatar,
}: {
  setActiveAvatar?: (actor: Actor, actorVariant?: ActorVariant) => void;
}) => {
  const { handleAvatarClick } = useAvatarClickHandler({
    setActiveAvatar: setActiveAvatar ?? (() => {}),
  });
  const [eventTracking] = useEventsContext();
  const { data: userData } = useUserContextQuery();
  const isTeamVoicesEnabled = useCanReadTeamVoices();
  const [, promptsDispatch] = usePromptsContext();

  const openAvatarModal = useCallback(
    (config: {
      event?: {
        type: EventName.ActorSelectionPromptOpened;
        data?: { path: string; programmatic?: boolean };
      };
      payload: ModalPayload;
    }) => {
      if (config?.event?.type) {
        eventTracking.track({
          type: config.event.type,
          ...(config.event.data ? { data: config.event.data } : {}),
        });
      }
      let projectVersion = config.payload.studioVersion;
      if (
        projectVersion === StudioProjectVersion.Universal ||
        projectVersion === StudioProjectVersion.ScriptStudio
      ) {
        projectVersion =
          config.payload.studioVersion === StudioProjectVersion.Universal
            ? StudioProjectVersion.Universal
            : StudioProjectVersion.V2;
      }
      promptsDispatch({
        type: 'OpenAvatarModalPrompt',
        payload: {
          activeActorId: config?.payload?.activeAvatarId || 0,
          activeVariantId: config?.payload?.activeVariantId || 0,
          activeVariantVersion: config?.payload?.activeVariantVersion,
          projectVersion,
          userData,
          enableTeamVoices: isTeamVoicesEnabled,
          onSelect: ({
            actor: selectedActor,
            variant,
            variantVersion,
            onClose,
          }) => {
            if (setActiveAvatar) {
              handleAvatarClick(
                selectedActor,
                'modal',
                variant,
                variantVersion,
                onClose
              );
              return;
            }
            if (config?.payload?.onSelect) {
              if (
                projectVersion === StudioProjectVersion.Universal &&
                config?.payload?.fromAvatar &&
                config?.payload.onSelect &&
                variant &&
                variantVersion
              ) {
                // Create a stable swap handler that can be reused
                const swapHandler = createAvatarSwapHandler({
                  currentFromAvatar: config.payload.fromAvatar,
                  promptsDispatch,
                  studioVersion: config.payload.studioVersion,
                  editor: config?.payload?.editor,
                  onSelect: props => {
                    config.payload.onSelect?.({
                      actor: props.actor,
                      variant: props.variant,
                      variantVersion: props.variantVersion,
                      onClose: props.onClose ?? (() => {}),
                      swapContext: props.swapContext,
                    });
                  },
                  tags: config?.payload?.tags ?? { avatarTags: [] },
                });
                // Call the handler with the selected avatar
                swapHandler({
                  actor: selectedActor,
                  variant,
                  variantVersion,
                  onClose,
                });
                return;
              }
              config.payload.onSelect?.({
                actor: selectedActor,
                variant,
                variantVersion,
                onClose: onClose ?? (() => {}),
              });
            }
          },
          tags: config?.payload?.tags ?? { avatarTags: [] },
        },
      });
    },
    [
      promptsDispatch,
      eventTracking,
      setActiveAvatar,
      handleAvatarClick,
      userData,
      isTeamVoicesEnabled,
    ]
  );

  return {
    handleAvatarClick,
    openAvatarModal,
  };
};

export default useStudioAvatarModal;
